import * as Yup from 'yup'
import { Form, Row, Collapse, Accordion, Table } from 'react-bootstrap'
import { CustomAccordionButton, CustomFormControl, CustomLink } from '../../../../StyledComponents';
import { useState } from 'react';
import { characterRestrictionInDocNamesRegExp } from '../ValidationRules';

function getFileSizeAsString(totalBytes: number): string {
    const Gigabyte = 1073741824
    const Megabyte = 1048576
    const Kilobyte = 1024

    if (totalBytes <= 0) return "0 Bytes"

    if (totalBytes >= Gigabyte) {
        return (totalBytes / Gigabyte).toFixed(2) + " GB"
    } else if (totalBytes >= Megabyte) {
        return (totalBytes / Megabyte).toFixed(2) + " MB"
    } else if (totalBytes >= Kilobyte) {
        return (totalBytes / Kilobyte).toFixed(2) + " kB"
    } else {
        return totalBytes + " Bytes"
    }
}

const checkRequiredDokument = (settings) => (Yup.mixed()
    .required(`Dokument ist erforderlich und darf nicht größer als ${getFileSizeAsString(settings.applicationFileSizeLimit)} sein.`)
    .test("FileSize", `Dokument darf nicht größer als ${getFileSizeAsString(settings.applicationFileSizeLimit)} sein.`, (file) => file && file.size < settings.applicationFileSizeLimit)
    .test("NullByteTest", "Die Datei darf nicht leer sein.", (file) => {
        if (file) {
            if (file.isValid !== undefined && file.isValid !== null && !file.isValid) return false;
            file.isValid = file.size > 0;
            if (!file.isValid) file.errMessage = "Die Datei darf nicht leer sein.";
            return file.isValid;
        }
    })
    .test("FileFormat", `Folgende Dateiformate sind erlaubt: ${settings.applicationPermittedExtensions.join(" ")}`, (file) => {
        if (file) {
            if (file.isValid !== undefined && file.isValid !== null && !file.isValid) return false;
            file.isValid = settings.applicationPermittedMimeTypes.includes(file.type);
            if (!file.isValid) file.errMessage = `Folgende Dateiformate sind erlaubt: ${settings.applicationPermittedExtensions.join(" ")}`;
            return file.isValid;
        }
    })
    .when("settings.isUnicode", {
        is: () => !settings.isUnicode,
        then: Yup.mixed()
            .test("FileNameTest",
                `Ungültiger Dokumentenname`,
                (file) => {
                    if (file) {
                        if (file.isValid !== undefined && file.isValid !== null && !file.isValid) return false;
                        file.isValid = file.name.match(characterRestrictionInDocNamesRegExp) !== null;
                        if (!file.isValid) file.errMessage = "Ungültiger Dokumentenname";
                        return file.isValid;
                    }
                })
    }));

export const Schema = settings => (Yup.object().shape({
    cvFile: checkRequiredDokument(settings),
    coveringLetterFile: checkRequiredDokument(settings),
    certificatesFile: checkRequiredDokument(settings),
    additionalFiles: Yup.array()
        .nullable()
        .test("FileSize",
            `Die Unterlagen dürfen höchstens ${settings.applicationFileNumberLimit} Dateien und zusammen nicht größer als ${getFileSizeAsString(settings.applicationFileSizeLimit)} sein.`,
            (files) => {
                if (files && files.length > 0) {
                    let totalSize: number = files.reduce((accumulator, currentFile) => accumulator + currentFile.size, 0);
                    return totalSize <= settings.applicationFileSizeLimit && files.length <= settings.applicationFileNumberLimit;
                } else {
                    return true;
                }
            })
        .test("FileFormat",
            `Folgende Dateiformate sind erlaubt: ${settings.applicationPermittedExtensions.join(" ")}`,
            (files) => {
                if (files && files.length > 0) {
                    let allValid = true;
                    for (let file of files) {
                        if (file.isValid === undefined || file.isValid === null) file.isValid = true;
                        if (!settings.applicationPermittedMimeTypes.includes(file.type)) {
                            file.isValid = false;
                            file.errMessage = `Folgende Dateiformate sind erlaubt: ${settings.applicationPermittedExtensions.join(" ")}`;
                            allValid = false;
                        }
                    }
                    return allValid;
                } else {
                    return true;
                }
            })
        .when("settings.isUnicode", {
            is: () => !settings.isUnicode,
            then: Yup.array().nullable()
                .test("FileNameTest",
                    `Ungültiger Dokumentenname`,
                    (files) => {
                        if (files && files.length > 0) {
                            let allValid = true;
                            for (let file of files) {
                                if (file.isValid === undefined || file.isValid === null) file.isValid = true;
                                if (!file.name.match(characterRestrictionInDocNamesRegExp)) {
                                    file.isValid = false;
                                    file.errMessage = `Ungültiger Dokumentenname`;
                                    allValid = false;
                                }
                            }
                            return allValid;
                        } else {
                            return true;
                        }
                    })
        }),
}));

class FileExt extends File {
    isValid: boolean = true;
    errMessage: string = "";
}

export const DocumentsSubForm = ({ form: { values, setFieldTouched, setFieldValue, handleBlur, handleChange, touched, errors }, ...props }) => {
    const { colors, settings } = props;
    const [cvDocument, setCvDocument] = useState<FileExt>();
    const [coveringLetter, setCoveringLetter] = useState<FileExt>();
    const [certificates, setCertificates] = useState<FileExt>();
    const [additionalDocuments, setAdditionalDocuments] = useState<FileExt[]>([]);

    function getTotalSizeOfAdditionalDocs(): number {
        if (!additionalDocuments || additionalDocuments?.length === 0) return 0

        let totalSize: number = 0
        for (let i = 0; i < additionalDocuments.length; i++) {
            const element = additionalDocuments[i];
            totalSize += element.size
        }
        return totalSize
    }

    function getTotalSizeOfDocs(): number {
        let totalSizeOfAddititionDocs = getTotalSizeOfAdditionalDocs();
        let cvDocumentSize = cvDocument ? cvDocument.size : 0;
        let coveringLetterSize = coveringLetter ? coveringLetter.size : 0;
        let certificatesSize = certificates ? certificates.size : 0;
        return totalSizeOfAddititionDocs + cvDocumentSize + coveringLetterSize + certificatesSize;
    }

    return (
        <Row>

            <Form.Group className="mb-3" controlId="formCvDoc">
                <Form.Label>Lebenslauf* {cvDocument && "(" + getFileSizeAsString(cvDocument.size) + ")"}</Form.Label>
                <CustomFormControl
                    colors={colors}
                    type="file"
                    required
                    name="cvFile"
                    onChange={(event) => {
                        if (event.target.files[0]) {
                            setCvDocument(event.target.files[0]);
                            setFieldTouched("cvFile");
                            setFieldValue("cvFile", event.target.files[0] || undefined);
                        }
                    }}
                    isValid={touched.cvFile && !errors.cvFile}
                    isInvalid={touched.cvFile && errors.cvFile}
                    accept={settings.applicationPermittedExtensions}
                />
                <Form.Control.Feedback type="invalid">
                    {errors.cvFile}
                </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3" controlId="formCoveringLetter">
                <Form.Label>Anschreiben* {coveringLetter && "(" + getFileSizeAsString(coveringLetter.size) + ")"}</Form.Label>
                <CustomFormControl
                    colors={colors}
                    type="file"
                    required
                    name="coveringLetterFile"
                    onChange={(event) => {
                        if (event.target.files[0]) {
                            setCoveringLetter(event.target.files[0]);
                            setFieldTouched("coveringLetterFile");
                            setFieldValue("coveringLetterFile", event.target.files[0] || undefined);
                        }
                    }}
                    isValid={touched.coveringLetterFile && !errors.coveringLetterFile}
                    isInvalid={touched.coveringLetterFile && errors.coveringLetterFile}
                    accept={settings.applicationPermittedExtensions}
                />
                <Form.Control.Feedback type="invalid">
                    {errors.coveringLetterFile}
                </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3" controlId="formCertificates">
                <Form.Label>Zeugnisse* {certificates && "(" + getFileSizeAsString(certificates.size) + ")"}</Form.Label>
                <CustomFormControl
                    colors={colors}
                    type="file"
                    required
                    name="certificatesFile"
                    onChange={(event) => {
                        if (event.target.files[0]) {
                            setCertificates(event.target.files[0]);
                            setFieldTouched("certificatesFile");
                            setFieldValue("certificatesFile", event.target.files[0] || undefined);
                        }
                    }}
                    isValid={touched.certificatesFile && !errors.certificatesFile}
                    isInvalid={touched.certificatesFile && errors.certificatesFile}
                    accept={settings.applicationPermittedExtensions}
                />
                <Form.Control.Feedback type="invalid">
                    {errors.certificatesFile}
                </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3" controlId="formAdditionalFiles">
                <Form.Label>Weitere Unterlagen {additionalDocuments && getTotalSizeOfAdditionalDocs() > 0 && "(" + getFileSizeAsString(getTotalSizeOfAdditionalDocs()) + ")"}</Form.Label>
                <CustomFormControl
                    colors={colors}
                    type="file"
                    multiple
                    name="additionalFiles"
                    onChange={(event) => {

                        if (event.target.files?.length) {
                            let files: FileExt[] = [];
                            if (additionalDocuments.length > 0) {
                                files = additionalDocuments.slice();
                            }
                            files.push(...Array.from<FileExt>(event.target.files));

                            setAdditionalDocuments(files);
                            setFieldTouched("additionalFiles");
                            setFieldValue("additionalFiles", files || undefined);
                        }

                    }}
                    isValid={touched.additionalFiles && !errors.additionalFiles}
                    isInvalid={errors.additionalFiles}
                    accept={settings.applicationPermittedExtensions} />
                <Form.Control.Feedback type="invalid">
                    {errors.additionalFiles}
                </Form.Control.Feedback>
                <Form.Text muted style={{ display: errors.additionalFiles ? "none" : "flex" }}>
                    Mehrfachauswahl möglich: Maximal {settings.applicationFileNumberLimit} Dateien (Lebenslauf + {settings.applicationFileNumberLimit-1} weitere Dateien) mit insgesamt {getFileSizeAsString(settings.applicationFileSizeLimit)}
                </Form.Text>
            </Form.Group>

            <div style={{ marginBottom: "16px" }}>
                <Collapse in={getTotalSizeOfDocs() > 0}>
                    <Accordion>
                        <Accordion.Item eventKey="0">
                            <CustomAccordionButton colors={colors}>Ausgewählte Dateien ({getFileSizeAsString(getTotalSizeOfDocs())})</CustomAccordionButton>
                            <Accordion.Body>
                                <Table>
                                    <tbody>
                                        {cvDocument &&
                                            <tr>
                                                <td style={{ wordBreak: "break-all" }} className={cvDocument.isValid ? "" : "text-danger"}><strong>{cvDocument.name}</strong></td>
                                                <td>{getFileSizeAsString(cvDocument.size)}</td>
                                                <td><CustomLink variant="link" size="sm" colors={colors}
                                                    onClick={() => {
                                                        setCvDocument(undefined);
                                                        setFieldTouched("cvFile");
                                                        setFieldValue("cvFile", undefined);

                                                        let inputElement = document.getElementById("formCvDoc") as HTMLInputElement;
                                                        inputElement.value = "";
                                                    }}
                                                >Entfernen</CustomLink></td>
                                                <td className="invalid" title={cvDocument.errMessage} style={{ visibility: cvDocument.isValid ? 'hidden' : 'visible' }}></td>
                                            </tr>
                                        }
                                        {coveringLetter &&
                                            <tr>
                                                <td style={{ wordBreak: "break-all" }} className={coveringLetter.isValid ? "" : "text-danger"}><strong>{coveringLetter.name}</strong></td>
                                                <td>{getFileSizeAsString(coveringLetter.size)}</td>
                                                <td><CustomLink variant="link" size="sm" colors={colors}
                                                    onClick={() => {
                                                        setCoveringLetter(undefined);
                                                        setFieldTouched("coveringLetterFile");
                                                        setFieldValue("coveringLetterFile", undefined);

                                                        let inputElement = document.getElementById("formCoveringLetter") as HTMLInputElement;
                                                        inputElement.value = "";
                                                    }}
                                                >Entfernen</CustomLink></td>
                                                <td className="invalid" title={coveringLetter.errMessage} style={{ visibility: coveringLetter.isValid ? 'hidden' : 'visible' }}></td>
                                            </tr>
                                        }
                                        {certificates &&
                                            <tr>
                                                <td style={{ wordBreak: "break-all" }} className={certificates.isValid ? "" : "text-danger"}><strong>{certificates.name}</strong></td>
                                                <td>{getFileSizeAsString(certificates.size)}</td>
                                                <td><CustomLink variant="link" size="sm" colors={colors}
                                                    onClick={() => {
                                                        setCertificates(undefined);
                                                        setFieldTouched("certificatesFile");
                                                        setFieldValue("certificatesFile", undefined);

                                                        let inputElement = document.getElementById("formCertificates") as HTMLInputElement;
                                                        inputElement.value = "";
                                                    }}
                                                >Entfernen</CustomLink></td>
                                                <td className="invalid" title={certificates.errMessage} style={{ visibility: certificates.isValid ? 'hidden' : 'visible' }}></td>
                                            </tr>
                                        }
                                        {additionalDocuments && Array.from(additionalDocuments).map((file: FileExt) =>
                                            <tr key={file.name}>
                                                <td style={{ wordBreak: "break-all" }} className={file.isValid === undefined || file.isValid ? "" : "text-danger"}>{file.name}</td>
                                                <td>{getFileSizeAsString(file.size)}</td>
                                                <td><CustomLink variant="link" size="sm" colors={colors}
                                                    onClick={() => {
                                                        let pos = -1;
                                                        for (let i = 0; i < additionalDocuments.length; i++) {
                                                            const f: FileExt = additionalDocuments[i]
                                                            if (f.name === file.name) {
                                                                pos = i;
                                                                break;
                                                            }
                                                        }

                                                        if (pos > -1) {
                                                            let files: FileExt[] = [];
                                                            files = additionalDocuments.slice();
                                                            files.splice(pos, 1);

                                                            setAdditionalDocuments(files);
                                                            setFieldValue("additionalFiles", files || undefined);

                                                            if (files.length === 0) {
                                                                let inputElement = document.getElementById("formAdditionalFiles") as HTMLInputElement;
                                                                inputElement.value = "";
                                                            }
                                                        }
                                                    }}
                                                >Entfernen</CustomLink></td>
                                                <td className="invalid" title={file.errMessage} style={{ visibility: file.isValid ? 'hidden' : 'visible' }}></td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </Accordion.Body>
                        </Accordion.Item>
                    </Accordion>
                </Collapse>
            </div>
        </Row>
    );
};
